import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import Header from './header';
import WelcomePage from './welcome-page';
import Footer from './footer';
import ActiveChat from 'app/concierge/panel/active-chat';
import ClarificationQuestionComponent from 'app/concierge/panel/clarification-question/ClarificationQuestion';
import { ClarificationQuestion as ClarificationQuestionType } from 'app/concierge/types';
import RecentChats from 'app/concierge/panel/recent-chats';
import { Resizable } from 're-resizable';
import {
    closeChat as setCloseChat,
    setConciergeWidgetWidth,
    setSelectedDomains,
    setCustomStyles,
    setIsAppPanel,
    clearNewRequestMessage,
    clearPrependContext,
    clearTestSession,
} from 'store/conciergeSlice';
import { hide, clearNewChat, setFromSearch } from 'store/conciergeSlice';
import type { IConciergeIssuesTarget } from 'store/conciergeSlice';
import type { ITestSession } from 'api/concierge';
import { setWaitingConcierge } from 'store/globalSearchSlice';
import { merge } from 'lodash';
import { tokenFormUpdatePropertyValue, uid } from 'tools/tools';
import { useConciergeChat } from '../hooks/useConciergeChat';
import { useHasConciergeDomains } from '../hooks/useHasConciergeDomains';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { useEntityIssues } from 'hooks/useEntityIssues';
import ReportConciergeContent from 'app/extreport/concierge/ReportConciergeContent';
import ClarificationsSection from 'components/common/clarifications/ClarificationsSection';
import TestSessionBanner from 'app/concierge/panel/test-session/TestSessionBanner';

import defaultStyles from './styles';

export interface ICustomStyles {
    panel?: SxProps<Theme>;
    welcomePage?: SxProps<Theme>;
    header?: SxProps<Theme>;
    footer?: SxProps<Theme>;
    footerDomainSelector?: SxProps<Theme>;
    activeChat?: SxProps<Theme>;
    activeChatMessage?: SxProps<Theme>;
    activeChatMessageResultItem?: SxProps<Theme>;
}

interface IProps {
    welcomeIcon?: string;
    welcomeIconWidth?: number;
    welcomeIconHeight?: number;
    botIcon?: string;
    title?: string;
    description?: string;
    startNewChatAfter?: number;
    showSourceSelector?: boolean;
    displayModelName?: boolean;
    suggestedPrompts?: string[];
    showSearchRequest?: boolean;
    includeContentSearch?: boolean;
    includeHelpDocs?: boolean;
    includeGlossaryTerms?: boolean;
    includeCustomFaq?: boolean;
    availableDomains?: string[];
    selectedDomains?: string[];
    isAppPanel?: boolean;
    customStyles?: ICustomStyles;
    loadMore?: boolean;
    extraUrlParams?: { [key: string]: string };
    useInfoButton?: boolean;
    hideHeader?: boolean;
    autoFocus?: boolean;
    getContext?: () => any | Promise<any>;
    customInstructions?: {
        mcp_post_processing?: string;
        llm_comment?: string;
    };
    onAction?: (
        action: string,
        payload: any,
    ) => Promise<{ success: boolean; message?: string }> | { success: boolean; message?: string };
}

export default function ConciergePanel({
    welcomeIcon,
    title,
    description,
    suggestedPrompts,
    botIcon,
    startNewChatAfter,
    displayModelName,
    showSourceSelector,
    welcomeIconHeight,
    welcomeIconWidth,
    showSearchRequest,
    availableDomains,
    includeContentSearch,
    includeCustomFaq,
    includeGlossaryTerms,
    includeHelpDocs,
    selectedDomains,
    isAppPanel,
    customStyles,
    loadMore,
    extraUrlParams,
    useInfoButton = false,
    hideHeader = false,
    autoFocus = true,
    getContext,
    customInstructions,
    onAction,
}: IProps) {
    const dispatch = useDispatch();
    const { t } = useBundleTranslation(['app/concierge/widget']);

    const styles = useMemo(() => {
        return merge({}, defaultStyles, { container: customStyles?.panel }) as typeof defaultStyles;
    }, [customStyles]);

    const visible = useSelector((state: any) => state.concierge.visible);
    const widgetWidth = useSelector((state: any) => state.concierge.width);
    const issuesTarget = useSelector((state: any) => state.concierge.issuesTarget as IConciergeIssuesTarget | null);

    const {
        context: issuesContext,
        answerQuestion,
        skipQuestion,
    } = useEntityIssues(issuesTarget?.id ?? 0, issuesTarget?.type ?? 'element', visible && !!issuesTarget);
    const newChat = useSelector((state: any) => state.concierge.newChat);
    const newRequestMessage = useSelector((state: any) => state.concierge.newRequestMessage);
    const prependContext = useSelector((state: any) => state.concierge.prependContext);
    const closeChat = useSelector((state: any) => state.concierge.closeChat);
    const testSession = useSelector((state: any) => state.concierge.testSession as ITestSession | null);

    const [activeTab, setActiveTab] = useState('concierge');
    const [answeredQuestionIds, setAnsweredQuestionIds] = useState<string[]>([]);

    const { hasDomains, isLoading: isDomainsLoading } = useHasConciergeDomains();

    const {
        dialog,
        setDialog,
        loading,
        activeChat,
        setActiveChat,
        onRequestSubmit,
        startNewChat: startNewChatBase,
        onAbort,
        domainsData,
        toolsData,
        onResendRequest,
        onRetryError,
        setLastInteraction,
        clearActiveChatRequest,
        silentAbort,
        startNewResponseBlock,
    } = useConciergeChat(
        {
            startNewChatAfter,
            includeContentSearch,
            includeHelpDocs,
            includeGlossaryTerms,
            includeCustomFaq,
            availableDomains,
            selectedDomains,
            isAppPanel,
            extraUrlParams,
            getContext,
            customInstructions,
        },
        visible,
    );

    const startNewChat = useCallback(() => {
        setAnsweredQuestionIds([]);
        startNewChatBase();
    }, [startNewChatBase]);

    const activeClarificationData = useMemo((): {
        question: ClarificationQuestionType['data'][number];
        itemUid: string;
        questionIndex: number;
        totalQuestions: number;
        isLast: boolean;
    } | null => {
        for (const item of dialog) {
            const allBlocks = item.parts.filter((p) => p.type === 'json' && p.jsonBlocks).flatMap((p) => p.jsonBlocks!);
            const clarBlock = allBlocks.find((b) => b.data_type === 'clarification_question') as
                ClarificationQuestionType | undefined;
            if (!clarBlock) continue;

            const questions = clarBlock.data;
            const currentIdx = questions.findIndex((q) => !answeredQuestionIds.includes(q.question_id));
            if (currentIdx === -1) continue;

            return {
                question: questions[currentIdx],
                itemUid: item.uid,
                questionIndex: currentIdx + 1,
                totalQuestions: questions.length,
                isLast: currentIdx === questions.length - 1,
            };
        }
        return null;
    }, [dialog, answeredQuestionIds]);

    const hideWelcomePageForReportClarifications = !!(
        issuesContext?.isEditAccess && issuesContext.clarifyingQuestions.length > 0
    );

    const handleClarificationAnswered = useCallback(
        (questionId: string, questionText: string, questionIndex: number, answer: string | null, isLast: boolean) => {
            setAnsweredQuestionIds((prev) => [...prev, questionId]);
            setDialog((prev) => {
                const newItems: typeof prev = [];
                if (questionIndex > 1) {
                    newItems.push({
                        uid: uid(),
                        type: 'response' as const,
                        parts: [{ type: 'markdown' as const, message: questionText, source: 'backend' as const }],
                        isClarificationQuestion: true,
                    });
                }
                newItems.push({
                    uid: uid(),
                    type: 'request' as const,
                    parts: [{ type: 'html' as const, message: answer ?? 'Skip', source: 'frontend' as const }],
                    isClarificationAnswer: true,
                });
                return [...prev, ...newItems];
            });
            if (isLast) {
                startNewResponseBlock();
            }
        },
        [startNewResponseBlock],
    );

    useEffect(() => {
        if (testSession) {
            silentAbort();
            clearActiveChatRequest();
            setDialog([]);
            setAnsweredQuestionIds([]);
            setActiveChat(testSession.session_uid);
        }
    }, [testSession?.session_uid]);

    useEffect(() => {
        if (newChat !== null && domainsData && toolsData) {
            silentAbort();
            clearActiveChatRequest();
            setDialog([]);
            setAnsweredQuestionIds([]);
            if (testSession) dispatch(clearTestSession());
            setActiveChat(Date.now());
            dispatch(clearNewChat());
            if (newChat) {
                onRequestSubmit(newChat);
            }
        }
    }, [newChat, domainsData, toolsData, clearActiveChatRequest, dispatch, silentAbort, onRequestSubmit, testSession]);

    useEffect(() => {
        if (!visible && testSession) {
            silentAbort();
            clearActiveChatRequest();
            dispatch(clearTestSession());
            setDialog([]);
            setAnsweredQuestionIds([]);
            setActiveChat(0);
        }
    }, [visible]);

    useEffect(() => {
        if (newRequestMessage && domainsData && toolsData) {
            if (!activeChat) {
                setActiveChat(Date.now());
            }
            dispatch(clearNewRequestMessage());
            onRequestSubmit(newRequestMessage);
        }
    }, [newRequestMessage, domainsData, toolsData, activeChat, onRequestSubmit, dispatch]);

    useEffect(() => {
        if (!testSession && prependContext?.showInChat && prependContext.text && dialog.length === 0 && activeChat) {
            const contextMessage = {
                uid: `prepend-context-${Date.now()}`,
                type: 'response' as const,
                parts: [
                    {
                        message: prependContext.text,
                        type: 'markdown' as const,
                        source: 'frontend' as const,
                    },
                ],
            };
            setDialog([contextMessage]);
        }
    }, [prependContext, dialog.length, activeChat, testSession]);

    const onClose = () => {
        setDialog([]);
        setAnsweredQuestionIds([]);
        setActiveChat(0);
        dispatch(setFromSearch(false));
        dispatch(hide());
        dispatch(setCloseChat(false));
        dispatch(clearNewChat());
        dispatch(setWaitingConcierge(false));
        dispatch(clearPrependContext());
        dispatch(clearTestSession());
    };

    useEffect(() => {
        if (closeChat) {
            onClose();
        }
    }, [closeChat]);

    useEffect(() => {
        dispatch(setCustomStyles(customStyles));
        dispatch(setIsAppPanel(isAppPanel));

        //@ts-ignore
        if (!window.tokenFormUpdatePropertyValue) {
            //@ts-ignore
            window.tokenFormUpdatePropertyValue = tokenFormUpdatePropertyValue;
        }

        return () => {
            //@ts-ignore
            delete window.tokenFormUpdatePropertyValue;
        };
    }, []);

    return visible ? (
        <Stack sx={styles.container}>
            <Resizable
                defaultSize={{
                    width: widgetWidth,
                    height: '100%',
                }}
                className={'hierarchy-resize-container'}
                minWidth={400}
                maxWidth={660}
                onResizeStart={(event, direction, elementRef) => {
                    elementRef.classList.add('resizing-active');
                }}
                onResizeStop={(event, direction, elementRef, delta) => {
                    const elementSize = elementRef.getBoundingClientRect();
                    elementRef.classList.remove('resizing-active');

                    dispatch(setConciergeWidgetWidth(elementSize.width));
                }}
                handleComponent={{
                    left: <Stack sx={styles.resizeHandler} />,
                }}
                handleStyles={{
                    left: styles.resizeStyle,
                }}
                enable={{
                    top: false,
                    right: false,
                    bottom: false,
                    left: true,
                    topRight: false,
                    bottomRight: false,
                    bottomLeft: false,
                    topLeft: false,
                }}
            >
                {!hideHeader && (
                    <Header
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        setLastInteraction={setLastInteraction}
                        botIcon={botIcon}
                    />
                )}
                {activeTab == 'concierge' && !isDomainsLoading && !hasDomains && !testSession && (
                    <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: 1,
                                p: 3,
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                            }}
                        >
                            <Box sx={{ fontSize: '32px', color: 'warning.main', lineHeight: 1 }}>
                                <IconMi icon="info-circle" />
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{t('no_sources_title')}</Typography>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', maxWidth: '280px' }}>
                                {t('no_sources')}
                            </Typography>
                        </Box>
                    </Stack>
                )}
                {activeTab == 'concierge' &&
                    issuesContext &&
                    !testSession &&
                    ('element' === issuesTarget?.type ? (
                        <ReportConciergeContent
                            context={issuesContext}
                            onAnswer={answerQuestion}
                            onSkip={skipQuestion}
                        />
                    ) : (
                        <ClarificationsSection
                            context={issuesContext}
                            onAnswer={answerQuestion}
                            onSkip={skipQuestion}
                            objectType={'dataset' === issuesTarget?.type ? 'dataset' : 'domain'}
                        />
                    ))}
                {activeTab == 'concierge' && (isDomainsLoading || hasDomains || !!testSession) && (
                    <>
                        {testSession && <TestSessionBanner session={testSession} />}
                        {!activeChat ? (
                            hideWelcomePageForReportClarifications ? (
                                // Keeps a growing child in the panel column so the footer
                                // stays pinned to the bottom while the welcome page is hidden.
                                <Box sx={{ flexGrow: 1 }} />
                            ) : (
                                <WelcomePage
                                    setActiveChat={setActiveChat}
                                    setActiveChatRequest={onRequestSubmit}
                                    welcomeIcon={welcomeIcon}
                                    suggestedPrompts={suggestedPrompts}
                                    title={title}
                                    description={description}
                                    welcomeIconWidth={welcomeIconWidth}
                                    welcomeIconHeight={welcomeIconHeight}
                                />
                            )
                        ) : (
                            <ActiveChat
                                key={activeChat}
                                dialog={dialog}
                                id={activeChat}
                                loading={loading}
                                showSearchRequest={showSearchRequest}
                                botIcon={botIcon}
                                loadMore={loadMore}
                                onUpdateDialog={setDialog}
                                useInfoButton={useInfoButton}
                                onResendRequest={onResendRequest}
                                onRetryError={onRetryError}
                                onAction={onAction}
                            />
                        )}
                        {activeClarificationData ? (
                            <ClarificationQuestionComponent
                                questionId={activeClarificationData.question.question_id}
                                question={activeClarificationData.question.question}
                                options={activeClarificationData.question.options}
                                botIcon={botIcon}
                                questionIndex={activeClarificationData.questionIndex}
                                totalQuestions={activeClarificationData.totalQuestions}
                                onAnswered={(answer) =>
                                    handleClarificationAnswered(
                                        activeClarificationData.question.question_id,
                                        activeClarificationData.question.question,
                                        activeClarificationData.questionIndex,
                                        answer,
                                        activeClarificationData.isLast,
                                    )
                                }
                            />
                        ) : (
                            <Footer
                                activeChat={activeChat}
                                setActiveChat={setActiveChat}
                                setActiveChatRequest={onRequestSubmit}
                                startNewChat={startNewChat}
                                onAbort={onAbort}
                                loading={loading}
                                loadingToSendNewRequestMessage={newRequestMessage && !domainsData && !toolsData}
                                showSourceSelector={showSourceSelector}
                                displayModelName={displayModelName}
                                autoFocus={autoFocus}
                            />
                        )}
                    </>
                )}
                {activeTab == 'recent' && (
                    <RecentChats setActiveChat={setActiveChat} setActiveChatRequest={onRequestSubmit} />
                )}
            </Resizable>
        </Stack>
    ) : null;
}
